import React from 'react';
import { fetchNavHTML, fetchShopCategories } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { GetServerSideProps } from 'next';

import { SecondaryNavType } from 'components/Nav/types';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WRelatedLinkView, WStoreNavView } from '@zola/svc-web-api-ts-client';

import { AppDownloadBannerMobile } from '@zola/zola-ui/src/components/AppDownloadBannerMobile/AppDownloadBannerMobile';
import Masthead, { MastheadProps } from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';
import Logos from '@zola/zola-ui/src/components/PreAuthV3/Logos';
import Differentiation from '@zola/zola-ui/src/components/PreAuthV3/Differentiation';
import CategoryTiles from '@zola/zola-ui/src/components/PreAuthV3/CategoryTiles';
import Nav from 'components/Nav';
import Header from 'components/LP/WeddingRegistry/Header';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import {
  desktopGridItems as desktopGridItemsAlt,
  mobileGridItems as mobileGridItemsAlt,
} from 'constants/registryv3/gridItemsAlt';
import { reviewItems } from 'constants/registryv3/reviewItems';
import { FAQItems } from 'constants/registryv3/faqItems';
import { registryCategoryItemsAlt } from 'constants/registryv3/registryCategoryItemsAlt';
import { differentiationContentAlt } from 'constants/registryv3/differentiationAlt';

import { logos } from 'constants/registryv3/logos';
import { collectionsCategoryItemAlt } from 'constants/registryv3/collectionsCategoryItemAlt';
import NextBackgroundForegroundImages from 'components/PreauthV3/NextBackgroundForegroundImages';

import NextLpGrid from 'components/PreauthV3/NextLpGrid';
import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import { desktopOnly, excludeDesktop } from '@zola/zola-ui/src/styles/emotion/mixins/responsive';
import { fetchSEORelatedLinks } from 'libs/server/seo';
import { getIsGuest } from 'libs/universal/utils/getIsGuest';
import Footer from 'components/ui/Footer/Footer';
import { IntroVideo } from 'components/ui/IntroVideo/IntroVideo';

import { WeddingRegistryContainer } from 'components/LP/WeddingRegistry/WeddingRegistry.styles';

const HeroBackground =
  '/static-preauth/images/WeddingRegistryLP/V3/Masthead/Desktop/HeroBackground.webp';

export type RegistryLPContainerProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  shopCategories: WStoreNavView;
  adviceLinks: WRelatedLinkView[];
};

const getMastheadProps = (): Partial<MastheadProps> => {
  return {
    altMedia: (
      <NextBackgroundForegroundImages
        backgroundImg={{
          src: HeroBackground,
          alt: '',
        }}
        priority
      />
    ),
  };
};

const RegistryLPContainer: React.FC<RegistryLPContainerProps> = (props): JSX.Element => {
  const { navHTML, userCtx, shopCategories, adviceLinks } = props;

  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  return (
    <WeddingRegistryContainer>
      <Header />
      {getIsGuest(userCtx) ? <AppDownloadBannerMobile campaignName="zola_web_lp_preauth" /> : <></>}
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        activePrimaryLink={NAV_DATA.PRIMARY.REGISTRY}
        shopCategories={shopCategories}
        secondaryNavType={SecondaryNavType.REGISTRY}
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Masthead
          btnHref="/onboard/new"
          btnText="Get started"
          secondaryBtnHref="/search/wedding-registry"
          secondaryBtnText="Find a couple"
          secondaryBtnAsLink
          isPhotoOnRight
          title={
            <h1>
              The <span>wedding registry</span>, reinvented
            </h1>
          }
          valueProps={[
            'Cash funds with zero fees and unlimited possibilities',
            'Shop all the top brands, gifts cards and experiences',
            'Easily add gifts from any store and share one link with guests',
          ]}
          {...getMastheadProps()}
        />

        <Logos {...logos} />
        <CategoryTiles
          title="Shop registry by category"
          categories={registryCategoryItemsAlt}
          showScrim
        />
        <NextLpGrid gridItems={desktopGridItemsAlt} css={desktopOnly} />
        <NextLpGrid gridItems={mobileGridItemsAlt} css={excludeDesktop} />
        <Differentiation
          content={differentiationContentAlt}
          title={{
            titleStart: 'The wedding registry that',
            highlightText: 'gifts',
            titleEnd: 'you more',
          }}
          isPhotoOnRight={false}
        />
        <CategoryTiles title="Shop by collection" categories={collectionsCategoryItemAlt} square />
        <div>
          <SupportAccordion
            advice={{ links: adviceLinks }}
            faqs={FAQItems}
            reviews={reviewItems}
            openSections={['faqs']}
          />
        </div>
        <Footer userCtx={userCtx} />
        <IntroVideo videoId="8ikYcJwkLp8" />
      </PageWrapper>
    </WeddingRegistryContainer>
  );
};

export default RegistryLPContainer;
export const getServerSideProps: GetServerSideProps<RegistryLPContainerProps> = async ({ req }) => {
  const [navHTML, userCtx, shopCategories, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchShopCategories(),
    fetchSEORelatedLinks({
      topicSlug: 'registry-gifts',
      subtopicSlug: 'wedding-registry-basics',
    }),
  ]);

  return {
    props: {
      navHTML,
      userCtx,
      shopCategories,
      adviceLinks,
    },
  };
};
